import React, { forwardRef, useCallback, useEffect, useState } from "react";

// js
import "../assets/js/main.min.js";

// css
import "../assets/css/main.min.css";
import "../assets/css/custom.css";

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

// component
import axios from "axios";
import { baseURL, key } from "../util/Config";
import { useDispatch, useSelector } from "react-redux";
import { getProfile } from "../store/admin/action.js";
import male from "../assets/images/male.png";
import moment from "moment";
import {
  getHostEarning,
  getHosthistory,
} from "../store/history/history.action.js";
import DailyIncome from "./history/DailyIncome.js";
import rCoin from "../assets/images/rcoin.png";
import { permissionError } from "../util/Alert.js";
import { Toast } from "../util/Toast.js";
import HostSettlement from "./HostSettlement.js";
import Diamond from "../assets/images/Diamond.svg";
import { useTranslation } from "react-i18next";

const MonthPickerInput = forwardRef(({ value, onClick, pillText }, ref) => (
  <button
    type="button"
    onClick={onClick}
    ref={ref}
    style={{
      border: "none",
      borderRadius: 999,
      padding: "10px 16px",
      fontWeight: 600,
      fontSize: 16,
      background: "#FFFFFF",
      color: pillText,
      boxShadow:
        "inset 0 0 0 1px rgba(0,0,0,0.05), 0 6px 16px rgba(0,0,0,0.25)",
      lineHeight: 1,
      cursor: "pointer",
      whiteSpace: "nowrap",
    }}
    className="shadow-sm"
  >
    {value}
  </button>
));

const Admin = () => {
  const UI = {
    bg: "#490B60",
    headerBg: "#490B60",
    cardEdge: "#372143",
    cardBg: "#3A124C",
    cardBgTop: "#48195E",
    accent: "#FFC524",
    subtext: "#EFCFE5",
    text: "#FFFFFF",
    tabInactive: "#9C86AC",
    pillText: "#EE0E74",
    gradient:
      "linear-gradient(90deg, rgba(117,75,214,1) 0%, rgba(255,64,122,1) 100%)",
    softGlow: "0 8px 24px rgba(255, 28, 96, 0.35), 0 2px 8px rgba(0,0,0,0.25)",
  };

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const admin = useSelector((state) => state.admin.seller);
  const hostId = localStorage.getItem("hostId");

  const currentMonth = moment().format("YYYY-MM");

  const [selectedDate, setSelectedDate] = useState(currentMonth);
  const [type, setType] = useState(1);
  const [isTableExpanded, setIsTableExpanded] = useState(false);
  const [coinTargets, setCoinTargets] = useState([]);
  const [coinTargetsError, setCoinTargetsError] = useState(false);
  const [salaryProgress, setSalaryProgress] = useState(null);
  const [salaryDollarBalance, setSalaryDollarBalance] = useState(0);

  const periodIncomeCoin = Number(
    salaryProgress?.lifetimeCoinsReceived ?? salaryProgress?.periodIncomeCoin ?? 0
  );
  const nextTargetCoin = 0;
  const remainingCoin = 0;
  const pendingSalaryUsd = 0;
  const projectedSalaryUsd = 0;
  const salaryStatus = salaryProgress?.status || "live";
  const cycleNumber = 0;

  const statusLabel = "Live USD";
  const statusColor = "#34D399";

  const INITIAL_ROWS_TO_SHOW = 5;
  const displayedTargets = [];

  const progressBarWidth = 100;

  const fetchSalaryProgress = useCallback(async () => {
    if (!hostId) return;
    try {
      const response = await axios.get(
        `${baseURL}salary/progress/me?userId=${hostId}`,
        { headers: { key } }
      );
      if (response.data?.status) {
        const data = response.data.data || {};
        const sharedBalance = Number(data.salaryDollarBalance ?? 0);
        const hostProgress = data.hostProgress || null;
        setSalaryProgress(
          hostProgress
            ? {
                ...hostProgress,
                salaryDollarBalance:
                  hostProgress.salaryDollarBalance ?? sharedBalance,
              }
            : null
        );
        setSalaryDollarBalance(
          Number(
            hostProgress?.salaryDollarBalance ?? sharedBalance ?? 0
          )
        );
      }
    } catch (error) {
      console.error("Error fetching salary progress:", error);
    }
  }, [hostId]);

  const fetchCoinTargets = useCallback(async () => {
    try {
      const response = await axios.get(
        `${baseURL}coinTarget?type=host&_t=${Date.now()}`,
        {
          headers: { key },
        }
      );
      if (response.data.status) {
        const targets = (response.data.coinTargets || []).sort(
          (a, b) => Number(a.coins || 0) - Number(b.coins || 0)
        );
        setCoinTargets(targets);
        setCoinTargetsError(false);
      } else {
        setCoinTargets([]);
        setCoinTargetsError(true);
      }
    } catch (error) {
      console.error("Error fetching coin targets:", error);
      setCoinTargets([]);
      setCoinTargetsError(true);
    }
  }, []);

  useEffect(() => {
    dispatch(getProfile(hostId));
    dispatch(getHosthistory(currentMonth, hostId));
    dispatch(getHostEarning(hostId, currentMonth));
    fetchCoinTargets();
    fetchSalaryProgress();
  }, [dispatch, currentMonth, hostId, fetchCoinTargets, fetchSalaryProgress]);

  useEffect(() => {
    const refreshHostData = () => {
      if (document.visibilityState === "visible") {
        fetchSalaryProgress();
        fetchCoinTargets();
        if (hostId) dispatch(getProfile(hostId));
      }
    };
    const onFocus = () => {
      fetchSalaryProgress();
      fetchCoinTargets();
      if (hostId) dispatch(getProfile(hostId));
    };
    document.addEventListener("visibilitychange", refreshHostData);
    window.addEventListener("focus", onFocus);
    return () => {
      document.removeEventListener("visibilitychange", refreshHostData);
      window.removeEventListener("focus", onFocus);
    };
  }, [fetchSalaryProgress, fetchCoinTargets, dispatch, hostId]);

  const handleDateChange = (date) => {
    const selectedDateObject = moment(date).format("YYYY-MM");
    setSelectedDate(selectedDateObject);
    dispatch(getHosthistory(selectedDateObject, hostId));
    dispatch(getHostEarning(hostId, selectedDateObject));
  };

  const handleBack = () => {
    window.showAndroidToast();
  };

  const monthPickerDate = moment(selectedDate, "YYYY-MM", true).isValid()
    ? moment(selectedDate, "YYYY-MM").toDate()
    : moment().toDate();

  const handleCopy = async (link) => {
    if (admin?.isActive === false) return permissionError();
    try {
      await navigator.clipboard.writeText(link);
      Toast("success", t("copy_success"));
    } catch (error) {
      console.error("Copy failed:", error);
      Toast("error", t("copy_failed"));
    }
  };

  const progressBarWidthUnused = (() => {
    return 100;
  })();

  const formatTargetCoins = (coins) => {
    const value = Number(coins || 0);
    if (value <= 0) return "0";
    return value.toLocaleString("en-US");
  };

  return (
    <>
      <div
        className="page-container"
        style={{ background: UI.bg, minHeight: "100vh" }}
      >
        <div className="page" style={{ background: UI.bg }}>
          <div className="main-wrapper p-0" style={{ background: UI.bg }}>
            <div className="main-section" style={{ background: UI.bg }}>
              <div
                className="d-flex align-items-center px-3 py-3 mb-2"
                style={{
                  zIndex: 9,
                  position: "fixed",
                  width: "100%",
                  top: 0,
                  left: 0,
                  right: 0,
                  background: UI.headerBg,
                  borderBottom: "1px solid rgba(255, 255, 255, 0.28)",
                  minHeight: 56,
                }}
              >
                <div
                  style={{
                    width: 40,
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 22 22"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    style={{ cursor: "pointer" }}
                  >
                    <path
                      d="M1.18529 11.648L7.60196 18.0647C7.77484 18.2317 8.0064 18.3241 8.24674 18.322C8.48709 18.3199 8.717 18.2235 8.88696 18.0535C9.05692 17.8836 9.15332 17.6537 9.15541 17.4133C9.1575 17.173 9.0651 16.9414 8.89812 16.7685L4.04621 11.9166H20.1667C20.4098 11.9166 20.643 11.82 20.8149 11.6481C20.9868 11.4762 21.0834 11.2431 21.0834 11C21.0834 10.7568 20.9868 10.5237 20.8149 10.3518C20.643 10.1799 20.4098 10.0833 20.1667 10.0833H4.04621L8.89812 5.23137C8.98568 5.14681 9.05551 5.04566 9.10355 4.93382C9.15159 4.82198 9.17688 4.7017 9.17794 4.57999C9.179 4.45827 9.1558 4.33757 9.10971 4.22491C9.06362 4.11226 8.99555 4.00991 8.90949 3.92384C8.82342 3.83777 8.72107 3.7697 8.60842 3.72361C8.49576 3.67752 8.37506 3.65433 8.25334 3.65539C8.13163 3.65645 8.01134 3.68173 7.8995 3.72978C7.78767 3.77782 7.68652 3.84765 7.60196 3.9352L1.18529 10.3519C1.01344 10.5238 0.916904 10.7569 0.916904 11C0.916904 11.243 1.01344 11.4761 1.18529 11.648Z"
                      fill="white"
                    />
                  </svg>
                </div>
                <p
                  className="fw-bold mb-0 text-center flex-grow-1"
                  style={{
                    fontSize: 16,
                    fontWeight: 700,
                    color: UI.text,
                    letterSpacing: 0.2,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    padding: "0 8px",
                  }}
                >
                  {t("host_center")}
                </p>
                <div style={{ width: 40, flexShrink: 0 }} />
              </div>

              <div className="" style={{ paddingTop: 72 }}>
                <div
                  className="responsive-bg"
                  style={{ minHeight: 88, position: "relative" }}
                >
                  <div
                    className="d-flex align-items-center p-3"
                    style={{ flexWrap: "nowrap", gap: 12 }}
                  >
                    <img
                      src={admin?.image ? admin?.image : male}
                      style={{
                        height: 60,
                        width: 60,
                        flexShrink: 0,
                        objectFit: "cover",
                        borderRadius: "50%",
                        border: "2px solid rgba(250, 250, 250, 1)",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
                      }}
                      alt=""
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p
                        className="mb-0 text-white"
                        style={{
                          fontSize: 18,
                          fontWeight: 700,
                          lineHeight: 1.2,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {admin?.name || "-"}
                      </p>
                      <p
                        className="mb-0 mt-1"
                        style={{
                          fontSize: 12,
                          color: UI.subtext,
                          fontWeight: 500,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {t("unique_id")} : {admin?.uniqueId || "-"}
                      </p>
                    </div>
                    <div
                      className="date-picker d-flex justify-content-end"
                      style={{ flexShrink: 0 }}
                    >
                      <DatePicker
                        selected={monthPickerDate}
                        dateFormat="yyyy/MM"
                        showMonthYearPicker
                        onChange={(date) => handleDateChange(date)}
                        customInput={
                          <MonthPickerInput pillText={UI.pillText} />
                        }
                      />
                    </div>
                  </div>
                </div>

                <div
                  className="mt-4"
                  style={{
                    border: `1px solid #ffffff4d`,
                    borderRadius: 16,
                    overflow: "hidden",
                    backdropFilter: "blur(2px)",
                    margin: "0px 10px",
                  }}
                >
                  <div
                    className="d-flex justify-content-between align-items-center px-3 py-3"
                    style={{ background: UI.cardBgTop }}
                  >
                    <h3
                      className="mt-1"
                      style={{
                        fontWeight: 600,
                        color: UI.accent,
                        fontSize: 16,
                        letterSpacing: 0.2,
                        marginBottom: 0,
                      }}
                    >
                      {t("diamond")}
                    </h3>
                    <p
                      className="mt-1 mb-0 d-flex align-items-center"
                      style={{
                        fontWeight: 600,
                        color: UI.accent,
                        fontSize: 16,
                      }}
                    >
                      <img
                        src={Diamond}
                        alt=""
                        height={22}
                        width={22}
                        style={{ marginRight: 8 }}
                      />
                      {Number(admin?.diamond || 0).toFixed(0)}
                    </p>
                  </div>

                  <div style={{ background: UI.cardBg }}>
                    <div
                      className="d-flex justify-content-between align-items-center px-3 py-3"
                      style={{
                        borderTop: `1px solid ${UI.cardEdge}`,
                        borderBottom: `1px solid ${UI.cardEdge}`,
                      }}
                    >
                      <h3
                        className="mt-1 mb-0"
                        style={{
                          fontWeight: 400,
                          fontSize: 14,
                          color: "#DCC8E8",
                        }}
                      >
                        {t("this_week_income")}
                      </h3>
                      <p
                        className="mt-1 mb-0 d-flex align-items-center"
                        style={{
                          fontWeight: 600,
                          color: UI.accent,
                          fontSize: 16,
                        }}
                      >
                        <img
                          src={rCoin}
                          alt=""
                          height={22}
                          width={22}
                          style={{ marginRight: 8 }}
                        />
                        {Number(admin?.rCoin || 0).toFixed(0)}
                      </p>
                    </div>
                    <div
                      className="d-flex justify-content-between align-items-center px-3 py-3"
                      style={{
                        borderTop: `1px solid ${UI.cardEdge}`,
                        borderBottom: `1px solid ${UI.cardEdge}`,
                      }}
                    >
                      <h3
                        className="mt-1 mb-0"
                        style={{
                          fontWeight: 400,
                          fontSize: 14,
                          color: "#DCC8E8",
                        }}
                      >
                        {t("spent_coin")}
                      </h3>
                      <p
                        className="mt-1 mb-0 d-flex align-items-center"
                        style={{
                          fontWeight: 600,
                          color: UI.accent,
                          fontSize: 16,
                        }}
                      >
                        <img
                          src={rCoin}
                          alt=""
                          height={22}
                          width={22}
                          style={{ marginRight: 8 }}
                        />
                        {Number(admin?.spentCoin || 0).toFixed(0)}
                      </p>
                    </div>
                  </div>
                </div>

                <div
                  className="mt-4"
                  style={{
                    border: `1px solid #ffffff4d`,
                    borderRadius: 16,
                    overflow: "hidden",
                    backdropFilter: "blur(2px)",
                    margin: "0px 10px",
                  }}
                >
                  <div
                    className="px-3 py-3"
                    style={{ background: UI.cardBgTop }}
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <h3
                        className="mt-1 mb-0"
                        style={{
                          fontWeight: 600,
                          color: UI.accent,
                          fontSize: 16,
                          letterSpacing: 0.2,
                        }}
                      >
                        {t("coin_target")}
                      </h3>
                      <button
                        type="button"
                        onClick={fetchSalaryProgress}
                        style={{
                          background: "transparent",
                          border: `1px solid ${UI.accent}`,
                          borderRadius: 8,
                          padding: "4px 10px",
                          color: UI.accent,
                          fontSize: 12,
                          fontWeight: 600,
                          cursor: "pointer",
                          flexShrink: 0,
                        }}
                      >
                        {t("refresh")}
                      </button>
                    </div>
                    <div
                      className="d-flex align-items-center mt-2"
                      style={{ gap: 8, flexWrap: "wrap" }}
                    >
                      {cycleNumber > 0 && (
                        <span
                          style={{
                            fontSize: 12,
                            color: UI.subtext,
                            fontWeight: 600,
                          }}
                        >
                          {t("cycle")} #{cycleNumber}
                        </span>
                      )}
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: statusColor,
                          background: "rgba(255,255,255,0.08)",
                          padding: "4px 8px",
                          borderRadius: 8,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {statusLabel}
                      </span>
                    </div>
                  </div>

                  <div style={{ background: UI.cardBg }}>
                    <div
                      className="px-3 py-3"
                      style={{ borderBottom: `1px solid ${UI.cardEdge}` }}
                    >
                      <div
                        className="d-flex flex-wrap"
                        style={{ gap: 12 }}
                      >
                        <div style={{ flex: "1 1 120px", minWidth: 100 }}>
                          <div style={{ fontSize: 11, color: "#9C86AC", marginBottom: 4 }}>
                            {t("period_income")}
                          </div>
                          <div style={{ fontSize: 15, fontWeight: 600, color: UI.accent }}>
                            {periodIncomeCoin.toLocaleString()}
                          </div>
                        </div>
                        <div style={{ flex: "1 1 120px", minWidth: 100 }}>
                          <div style={{ fontSize: 11, color: "#9C86AC", marginBottom: 4 }}>
                            Lifetime USD
                          </div>
                          <div style={{ fontSize: 15, fontWeight: 600, color: "#34D399" }}>
                            ${Number(salaryProgress?.lifetimeUsd || 0).toFixed(6)}
                          </div>
                        </div>
                        <div style={{ flex: "1 1 120px", minWidth: 100 }}>
                          <div style={{ fontSize: 11, color: "#9C86AC", marginBottom: 4 }}>
                            {t("salary_balance")}
                          </div>
                          <div style={{ fontSize: 15, fontWeight: 600, color: UI.text }}>
                            ${Number(salaryDollarBalance || 0).toFixed(6)}
                          </div>
                        </div>
                      </div>
                      <div className="mt-2" style={{ fontSize: 12, color: UI.subtext }}>
                        USD credits instantly on every gift (linear rate). No pending approval.
                      </div>
                    </div>

                    <div className="px-3 py-3" style={{ display: "none" }}>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span
                          style={{
                            fontSize: 14,
                            color: "#DCC8E8",
                            fontWeight: 600,
                          }}
                        >
                          {t("target_reached")}
                        </span>
                        <span
                          style={{
                            fontSize: 14,
                            color: UI.accent,
                            fontWeight: 600,
                          }}
                        >
                          100%
                        </span>
                      </div>
                      <div
                        style={{
                          width: "100%",
                          height: 12,
                          backgroundColor: UI.cardEdge,
                          borderRadius: 6,
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            height: "100%",
                            width: `100%`,
                            background: UI.gradient,
                            borderRadius: 6,
                            transition: "width 0.3s ease",
                          }}
                        />
                      </div>
                      <div className="d-flex justify-content-between align-items-center mt-2">
                        <span style={{ fontSize: 12, color: "#9C86AC" }}>
                          {periodIncomeCoin.toLocaleString()} {t("coins")}
                        </span>
                        <span style={{ fontSize: 12, color: "#9C86AC" }}>
                          Live auto-credit — no pending target
                        </span>
                      </div>
                    </div>

                    <div
                      className="px-3 pb-3"
                      style={{ borderTop: `1px solid ${UI.cardEdge}`, display: "none" }}
                    >
                      <div
                        style={{
                          fontSize: 14,
                          color: "#DCC8E8",
                          fontWeight: 400,
                          marginBottom: 12,
                          marginTop: 12,
                        }}
                      >
                        {t("coin_target_information")}
                      </div>
                      <div
                        style={{
                          border: `1px solid ${UI.cardEdge}`,
                          borderRadius: 8,
                          overflow: "hidden",
                        }}
                      >
                        <table
                          style={{
                            width: "100%",
                            borderCollapse: "collapse",
                          }}
                        >
                          <thead>
                            <tr
                              style={{
                                background: UI.cardBgTop,
                                borderBottom: `1px solid ${UI.cardEdge}`,
                              }}
                            >
                              <th
                                style={{
                                  padding: "12px",
                                  textAlign: "center",
                                  fontSize: 13,
                                  fontWeight: 600,
                                  color: UI.accent,
                                  width: "60px",
                                }}
                              >
                                {t("sl_no")}
                              </th>
                              <th
                                style={{
                                  padding: "12px",
                                  textAlign: "left",
                                  fontSize: 13,
                                  fontWeight: 600,
                                  color: UI.accent,
                                }}
                              >
                                {t("coin_target")}
                              </th>
                              <th
                                style={{
                                  padding: "12px",
                                  textAlign: "right",
                                  fontSize: 13,
                                  fontWeight: 600,
                                  color: UI.accent,
                                }}
                              >
                                {t("host_salary_usdt")}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {displayedTargets.length === 0 ? (
                              <tr>
                                <td
                                  colSpan={3}
                                  style={{
                                    padding: "16px",
                                    textAlign: "center",
                                    fontSize: 14,
                                    color: "#9C86AC",
                                  }}
                                >
                                  {coinTargetsError
                                    ? t("failed_to_load_data") ||
                                      "Failed to load coin targets. Pull to refresh."
                                    : t("no_data_found")}
                                </td>
                              </tr>
                            ) : (
                              displayedTargets.map((target, displayIndex) => {
                              const originalIndex = coinTargets.indexOf(target);
                              const targetCoins = Number(target.coins || 0);
                              const isReached = periodIncomeCoin >= targetCoins;
                              const coinsFormatted = formatTargetCoins(targetCoins);
                              return (
                                <tr
                                  key={originalIndex}
                                  style={{
                                    borderBottom:
                                      displayIndex < displayedTargets.length - 1
                                        ? `1px solid ${UI.cardEdge}`
                                        : "none",
                                    background: isReached
                                      ? "rgba(255, 197, 36, 0.15)"
                                      : originalIndex % 2 === 0
                                        ? "rgba(156, 134, 172, 0.1)"
                                        : "transparent",
                                  }}
                                >
                                  <td
                                    style={{
                                      padding: "12px",
                                      textAlign: "center",
                                      fontSize: 14,
                                      color: isReached ? UI.accent : "#DCC8E8",
                                      fontWeight: isReached ? 600 : 400,
                                    }}
                                  >
                                    {originalIndex + 1}
                                  </td>
                                  <td
                                    style={{
                                      padding: "12px",
                                      fontSize: 14,
                                      color: isReached ? UI.accent : "#DCC8E8",
                                      fontWeight: isReached ? 600 : 400,
                                    }}
                                  >
                                    {coinsFormatted}
                                  </td>
                                  <td
                                    style={{
                                      padding: "12px",
                                      textAlign: "right",
                                      fontSize: 14,
                                      color: isReached ? UI.accent : "#DCC8E8",
                                      fontWeight: isReached ? 600 : 400,
                                    }}
                                  >
                                    ${Number(target.amount || 0)}
                                  </td>
                                </tr>
                              );
                            })
                            )}
                          </tbody>
                        </table>
                      </div>

                      {coinTargets.length > INITIAL_ROWS_TO_SHOW && (
                        <div
                          className="d-flex justify-content-center mt-3"
                          style={{ paddingBottom: "12px" }}
                        >
                          <button
                            onClick={() => setIsTableExpanded(!isTableExpanded)}
                            style={{
                              background: "transparent",
                              border: `1px solid ${UI.accent}`,
                              borderRadius: 8,
                              padding: "10px 24px",
                              color: UI.accent,
                              fontSize: 14,
                              fontWeight: 600,
                              cursor: "pointer",
                            }}
                          >
                            {isTableExpanded ? t("show_less") : t("show_more")}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="p-2 mt-3 responsive-bg2">
                  <div className="d-flex align-items-center justify-content-between p-2">
                    <span
                      className="fw-bold mb-0 "
                      style={{
                        fontSize: 16,
                        fontWeight: 600,
                        color: "#FFFFFF",
                        padding: "8px 16px",
                        borderRadius: 10,
                        border: "none",
                      }}
                    >
                      {t("my_agency")}
                    </span>

                    <div className="text-end">
                      <div className="d-flex align-items-center justify-content-end">
                        <img
                          src={
                            !admin?.hostAgency?.image?.includes("\\")
                              ? admin?.hostAgency?.image
                              : male
                          }
                          style={{
                            height: 30,
                            width: 30,
                            objectFit: "cover",
                            borderRadius: "44px",
                            border: "1px solid rgba(255, 255, 255, 0.94)",
                          }}
                          alt=""
                        />
                        <h6
                          className="fw-bold ms-2 mb-0 text-white"
                          style={{ fontSize: 16, fontWeight: 600 }}
                        >
                          {admin?.hostAgency?.name
                            ? admin?.hostAgency?.name
                            : "-"}
                        </h6>
                      </div>

                      <div className="d-flex align-items-center justify-content-end mt-2">
                        <h6
                          className="fw-bold mb-0 text-white"
                          style={{
                            fontSize: 14,
                            fontWeight: 500,
                            opacity: 0.95,
                          }}
                        >
                          {t("id")} :{" "}
                          {admin?.hostAgency?.uniqueId
                            ? admin?.hostAgency?.uniqueId
                            : "-"}
                        </h6>
                        <button
                          className="ms-1 pe-0"
                          style={{
                            border: "none",
                            backgroundColor: "transparent",
                            lineHeight: 0,
                          }}
                          onClick={() =>
                            handleCopy(admin?.hostAgency?.uniqueId)
                          }
                          aria-label="Copy agency ID"
                          title="Copy"
                        >
                          <svg
                            width="20"
                            height="20"
                            viewBox="0 0 16 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M10 2.5H3C2.17157 2.5 1.5 3.17157 1.5 4V14C1.5 14.8284 2.17157 15.5 3 15.5H10C10.8284 15.5 11.5 14.8284 11.5 14V4C11.5 3.17157 10.8284 2.5 10 2.5Z"
                              fill="white"
                            />
                            <path
                              d="M14.4998 2V12C14.4987 12.3975 14.3402 12.7783 14.0592 13.0593C13.7781 13.3404 13.3973 13.4988 12.9998 13.5H12.4998V4C12.4998 3.33696 12.2365 2.70107 11.7676 2.23223C11.2988 1.76339 10.6629 1.5 9.99984 1.5H4.58984C4.693 1.20823 4.88388 0.955499 5.13631 0.776469C5.38874 0.597439 5.69037 0.50087 5.99984 0.5H12.9998C13.3973 0.501187 13.7781 0.659603 14.0592 0.940651C14.3402 1.2217 14.4987 1.60254 14.4998 2Z"
                              fill="white"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  className="d-flex justify-content-around mt-3 mb-0"
                  style={{
                    background: "#361746",
                    padding: "14px 5px 0px 5px",
                  }}
                >
                  <TabLabel
                    label={t("payment_history")}
                    active={type === 2}
                    onClick={() => setType(2)}
                    UI={UI}
                  />
                  <TabLabel
                    label={t("daily_income")}
                    active={type === 1}
                    onClick={() => setType(1)}
                    UI={UI}
                  />
                </div>

                {type === 1 && <DailyIncome />}
                {type === 2 && <HostSettlement />}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const TabLabel = ({ label, active, onClick, UI }) => {
  return (
    <div
      className="ms-3 text-center"
      style={{ paddingBottom: 0, cursor: "pointer", minWidth: 96 }}
      onClick={onClick}
    >
      <div
        className="mb-0"
        style={{
          fontSize: 14,
          fontWeight: active ? 500 : 400,
          color: active ? "#FF0C8A" : UI.tabInactive,
          transition: "color .2s ease",
        }}
      >
        {label}
      </div>
      <div
        style={{
          height: 3,
          width: 56,
          margin: "8px auto 0px auto",
          borderRadius: "5px 5px 0px 0px",
          background: active ? "#FF0C8A" : "transparent",
          boxShadow: active ? "0 2px 8px rgba(255,28,96,.5)" : "none",
          transition: "all .2s ease",
        }}
      />
    </div>
  );
};

export default Admin;
